import React from 'react';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
// Material UI
import { DatePicker as MuiDatePicker } from '@material-ui/pickers';
// Custom
import { dateFormatter } from 'utils/DateUtils';
import { CloseIconButton } from 'components/buttons/IconButtons';
import MuiInputAdornment from '@material-ui/core/InputAdornment';

// Label formatter
const handleFormatLabel = (date, viewType) => {
  if (!date) {
    return '';
  }
  switch (viewType) {
    case 'date':
      return dateFormatter.toLocalizedDate(date);
    case 'yearAndMonth':
      return dateFormatter.toMonthAndYear(date);
    case 'year':
      return dateFormatter.toYear(date);
    default:
      return '';
  }
};

const DatePicker = ({
  variant,
  label,
  value,
  minDate,
  maxDate,
  errorText,
  clearable,
  required,
  disabled,
  fullWidth,
  margin,
  viewType,
  onChange,
  isError,
  icon,
  placeholder,
}) => {
  // Error flag
  const error = !_isEmpty(errorText) || isError;

  // Component props
  let componentProps = {
    value,
    disabled,
    variant,
    autoOk: variant === 'inline',
    onChange,
    placeholder,
  };
  if (minDate) {
    componentProps = {
      ...componentProps,
      minDate,
    };
  }
  if (maxDate) {
    componentProps = {
      ...componentProps,
      maxDate,
    };
  }
  if (variant === 'dialog') {
    componentProps = {
      ...componentProps,
      cancelLabel: <LocalizedMessage id="button.cancel" />,
    };
  }
  if (viewType !== 'date') {
    switch (viewType) {
      case 'yearAndMonth':
        componentProps = {
          ...componentProps,
          views: ['year', 'month'],
          openTo: 'month',
        };
        break;
      case 'year':
        componentProps = {
          ...componentProps,
          views: ['year'],
          openTo: 'year',
        };
        break;
      default:
        break;
    }
  }

  // Internal TextField props
  const textFieldProps = {
    label,
    error,
    helperText: errorText,
    required,
    fullWidth,
    margin,
  };

  let clearableTopPosition = 9;
  if (margin === 'normal') {
    clearableTopPosition = 24;
  }

  return (
    <React.Fragment>
      <MuiDatePicker
        labelFunc={(date) => handleFormatLabel(date, viewType)}
        {...componentProps}
        {...textFieldProps}
        InputProps={{
          startAdornment: icon ? <MuiInputAdornment position="start">{icon}</MuiInputAdornment> : null,
        }}
      />
      {clearable && value && (
        <div style={{ position: 'absolute', top: clearableTopPosition, right: 14 }}>
          <CloseIconButton onClick={() => onChange(null)} tooltipId="tooltip.empty" />
        </div>
      )}
    </React.Fragment>
  );
};

DatePicker.propTypes = {
  variant: PropTypes.oneOf(['dialog', 'inline', 'static']),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  value: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  errorText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  isError: PropTypes.bool,
  clearable: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),
  viewType: PropTypes.oneOf(['date', 'yearAndMonth', 'year']),
  onChange: PropTypes.func.isRequired,
  icon: PropTypes.element,
  placeholder: PropTypes.string,
};

DatePicker.defaultProps = {
  variant: 'inline',
  label: null,
  value: null,
  minDate: null,
  maxDate: null,
  errorText: null,
  isError: false,
  clearable: false,
  required: false,
  disabled: false,
  fullWidth: true,
  margin: 'normal',
  viewType: 'date',
  icon: null,
  placeholder: null,
};

export default React.memo(DatePicker);



// WEBPACK FOOTER //
// ./components/core/DatePicker.js